import { SendIntent } from 'send-intent'
import { Capacitor } from '@capacitor/core'
import bus from './eventbus'
import router from './router'

const URL_RE = /(https?:\/\/[^\s]+)/g

function extractTitle(titleString) {
  if (!titleString) return null

  let title = titleString
  // Try to decode the title, but don't fail if it contains invalid percent encoding
  try {
    title = decodeURIComponent(titleString)
  } catch (e) {
    // If decoding fails (e.g., "30%" in title), just use the original string
    title = titleString
  }

  // if we find a URL, delete it
  const titleWithoutUrls = title.replaceAll(URL_RE, '').trim()

  return titleWithoutUrls || null
}

function extractUrlFromIntent(intent) {
  // Helper function to clean and normalize URLs
  const cleanUrl = url => {
    if (!url) return null

    // Remove any zero-width spaces or invisible characters
    let cleanedUrl = url.replace(/[\u200B-\u200D\uFEFF]/g, '')

    // Trim whitespace
    cleanedUrl = cleanedUrl.trim()

    // Handle potential Android intent URL encoding issues
    // Sometimes URLs come with android-app:// prefix or other wrappers
    if (cleanedUrl.includes('http://') || cleanedUrl.includes('https://')) {
      const match = cleanedUrl.match(/(https?:\/\/[^\s)]+)/i)
      if (match) {
        const [matchedUrl] = match
        cleanedUrl = matchedUrl
      }
    }

    return cleanedUrl
  }

  // Check direct URL field - but also extract URL if it contains other text
  if (intent.url) {
    const cleaned = cleanUrl(intent.url)
    if (cleaned) {
      // First check if it's a clean URL
      try {
        const validUrl = new URL(cleaned)
        return validUrl.href
      } catch {
        // If not, try to extract URL from the text
        const extractedUrl = cleaned.match(URL_RE)?.[0]
        if (extractedUrl) return extractedUrl
      }
    }
  }

  if (intent.additionalItems?.[0]?.url) {
    const cleaned = cleanUrl(intent.additionalItems[0].url)
    if (cleaned) {
      try {
        // Try to decode the URL first (Vivaldi sends encoded URLs)
        const decodedUrl = decodeURIComponent(cleaned)
        const validUrl = new URL(decodedUrl)
        return validUrl.href
      } catch {
        // If decoding fails, try without decoding
        try {
          const validUrl = new URL(cleaned)
          return validUrl.href
        } catch {
          // Last resort: extract URL pattern
          const extractedUrl = cleaned.match(URL_RE)?.[0]
          if (extractedUrl) return extractedUrl
        }
      }
    }
  }

  // Check text field (commonly used by Steam, AliExpress, Amazon)
  if (intent.text) {
    const cleaned = cleanUrl(intent.text)
    if (cleaned) {
      const textUrl = cleaned.match(URL_RE)?.[0]
      if (textUrl) return textUrl
    }
  }

  // Check additional items for text field
  if (intent.additionalItems?.[0]?.text) {
    const cleaned = cleanUrl(intent.additionalItems[0].text)
    if (cleaned) {
      const itemTextUrl = cleaned.match(URL_RE)?.[0]
      if (itemTextUrl) return itemTextUrl
    }
  }

  // Fallback to title field
  const title = intent.title || intent.additionalItems?.[0]?.title
  if (!title) return null

  const cleaned = cleanUrl(title)
  if (!cleaned) return null

  try {
    const decodedUrl = decodeURIComponent(cleaned)
    // look for URL in title
    const url = decodedUrl.match(URL_RE)?.[0]
    return url || null
  } catch {
    // If decoding fails, try without decoding
    const url = cleaned.match(URL_RE)?.[0]
    return url || null
  }
}

async function checkIntent(app) {
  console.log('Checking Intent')

  try {
    const result = await SendIntent.checkSendIntentReceived()

    if (!result) {
      console.error('No SendIntent found')
      return
    }

    console.log('SendIntent found', JSON.stringify(result))

    // Enhanced logging for debugging intents
    console.log('Intent fields:', {
      url: result.url,
      text: result.text,
      title: result.title,
      type: result.type,
      additionalItems: result.additionalItems,
    })

    const payload = result.url ? result : result.additionalItems?.[0]
    const url = extractUrlFromIntent(result)

    if (!url) {
      // Check if this is a text-only share (no URL present)
      const textContent = result.content || result.title || result.text || ''
      const intentType = result.type || result.additionalItems?.[0]?.type || ''

      if (textContent.trim() && (intentType === 'text/plain' || !intentType)) {
        console.log('Text-only intent detected, routing to note creation')

        const noteTitle = textContent.trim().substring(0, 100)

        app.config.globalProperties.$store.dispatch('native/onNewIntent', {
          type: 'note',
          text: textContent,
          title: noteTitle,
          result: {
            ...result,
            title: noteTitle,
            text: textContent,
            type: 'text/plain',
          },
          finish: () => {
            SendIntent.finish()
            app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
          },
        })

        router.replace('/notes/add').catch(() => {})
        return
      }

      console.error('No URL found in intent and not a text share', result)
      bus.$emit('toast:error', 'Could not process shared content')
      return
    }

    console.log('Extracted URL:', url)

    // Try to create URL object without decoding first (might already be decoded)
    let urlObject
    try {
      urlObject = new URL(url)
    } catch (firstError) {
      // If that fails, try with decoding
      try {
        urlObject = new URL(decodeURIComponent(url))
      } catch (secondError) {
        // If both fail, just use the string as-is if it looks like a URL
        if (url.match(/^https?:\/\//)) {
          urlObject = { toString: () => url }
        } else {
          console.error('Error resolving URL', url, 'First:', firstError.message, 'Second:', secondError.message)
          vm.$bus.$emit('toast:error', vm.$t('error.invalid_link'))
          return
        }
      }
    }

    if (urlObject) {
      const parsedData = {
        ...payload,
        title: extractTitle(payload?.title || result.title)?.slice(0, 100) ?? undefined, // TODO 100 should be a const
        url: urlObject.toString(),
      }

      app.config.globalProperties.$store.dispatch('native/onNewIntent', {
        url: parsedData.url,
        result: parsedData,
        finish: () => {
          //if (Capacitor.getPlatform() === 'android') {
            SendIntent.finish()
          //}
          app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
        },
      })

      router.replace('/bookmarks/add').catch(() => {})
    }
  } catch (error) {
    console.error('Error handling sendIntent:', error, error.message)
    // Show user-friendly error message
    vm.$bus.$emit('toast:error', vm.$t('error.invalid_link'))
  }
}

bus.$once('app:created', async app => {
  if (app.config.globalProperties.$isNative) {
    checkIntent(app)

    window.addEventListener('sendIntentReceived', async () => {
      console.log('sendIntent received')
      checkIntent(app)
    })
  }
})
